import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Clock, CalendarDays, ChevronLeft, Landmark, Footprints } from "lucide-react";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";
import PronounceButton from "@/components/PronounceButton";
import TrailMap from "@/components/TrailMap";
import { EventCard } from "@/components/home/EventsSection";

export default function VenueDetail() {
  const { venueId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/venues/${venueId}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [venueId]);

  if (notFound) return <div className="py-24 text-center text-slate-500" data-testid="venue-not-found">Venue not found. <Link to="/explore" className="text-pink-600 font-bold">Browse the world</Link></div>;
  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { venue: v, country, events, nearby } = data;
  const cityUrl = country && v.city_slug ? `/places/${country.slug}/cities/${v.city_slug}` : null;

  return (
    <div data-testid={`venue-page-${venueId}`}>
      <div className="relative h-64 md:h-80">
        <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          {cityUrl && (
            <Link to={cityUrl} data-testid="venue-back-link" className="inline-flex items-center gap-1 text-xs font-bold text-pink-300 hover:text-pink-200 mb-2">
              <ChevronLeft className="w-3.5 h-3.5" /> {country.flag} {v.city}, {country.name}
            </Link>
          )}
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-300">{v.type}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white flex items-center gap-3 flex-wrap">
            {v.name} <PronounceButton text={v.name} />
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <p className="text-slate-600 leading-relaxed" data-testid="venue-blurb">{v.blurb}</p>
            <AIContentBlock pageKey={`venue-${venueId}`} topic={`${v.name}, a ${v.type?.toLowerCase() || "music venue"} in ${v.city} — its role in the local music scene`} imgQuery={`${v.name} ${v.city} live music venue`} compact />
            {v.lat && v.lng && (
              <div className="mt-6">
                <TrailMap stops={[v]} />
              </div>
            )}
          </div>
          <div className="space-y-5">
            <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="venue-hours-card">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-purple-600" /> Opening hours</p>
              {v.hours ? (
                <p className="text-sm text-slate-700 leading-relaxed font-medium">{v.hours}</p>
              ) : (
                <p className="text-sm text-slate-500">Hours not published yet — know them? <Link to="/community?kind=correction" className="text-pink-600 font-bold">Suggest a correction</Link></p>
              )}
            </div>
            <div className="bg-white rounded-3xl border border-slate-100 p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-pink-600" /> Location</p>
              <p className="text-sm text-slate-700 font-medium">{v.city}{country ? `, ${country.name}` : ""}</p>
              {v.lat && v.lng && <p className="text-xs text-slate-400 mt-1">{v.lat.toFixed(4)}, {v.lng.toFixed(4)}</p>}
              {cityUrl && (
                <Link to={`${cityUrl}/trail`} data-testid="venue-trail-link" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-purple-50 text-purple-700 text-xs font-black hover:bg-purple-100 transition-colors">
                  <Footprints className="w-3.5 h-3.5" /> Part of the {v.city} Music Trail
                </Link>
              )}
            </div>
          </div>
        </div>

        <section className="mt-10" data-testid="venue-sessions-section">
          <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2 mb-5">
            <span className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"><CalendarDays style={{ width: 18, height: 18 }} /></span>
            Upcoming Sessions &amp; Events in {v.city}
          </h2>
          {events.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {events.map((ev) => <EventCard key={ev.id} ev={ev} />)}
            </div>
          ) : (
            <p className="text-sm text-slate-500 bg-white rounded-2xl border border-slate-100 p-5" data-testid="venue-sessions-empty">
              No upcoming sessions listed yet. <Link to="/community?kind=event" className="text-pink-600 font-bold">Submit an event</Link> happening here.
            </p>
          )}
        </section>

        {nearby.length > 0 && (
          <section className="mt-10" data-testid="venue-nearby-section">
            <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2 mb-5">
              <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center"><Landmark style={{ width: 18, height: 18 }} /></span>
              More Venues in {v.city}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {nearby.map((n) => (
                <Link key={n.id} to={`/venues/${n.id}`} className="card-lift bg-white rounded-2xl border border-slate-100 overflow-hidden group" data-testid={`nearby-venue-${n.id}`}>
                  <div className="h-28 overflow-hidden"><img src={n.image} alt={n.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">{n.type}</p>
                    <h3 className="font-bold text-slate-900 mt-1 text-sm">{n.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
